import { Alert, Form, Input, Modal, Select } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import CanonicalTagSelect from "../CanonicalTagSelect";
import {
  CREATE_MAPPING_INFO_TEXT,
  METADATA_FIELDS_ENDPOINT_PATH,
  TAG_DOMAIN_FILTER_OPTIONS,
  TAGS_API_BASE
} from "../helper";
import MetadataFields from "../MetadataFields";

/**
 * Adds one mapping by hand: the source's own tag, our tag, and whatever extra
 * fields the chosen product line records.
 *
 * A source tag this product line already maps is refused by the backend on its
 * own unique index, and that refusal is shown against the source tag field
 * rather than as a passing toast, since the field is the thing to change.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 * @param {boolean} props.isOpen
 * @param {string} [props.fixedTagDomain] - The product line the mapping joins when the screen has
 *   pinned or selected one. Absent, the form asks for a required Type and scopes the canonical tag
 *   picker and the metadata inputs to it.
 * @param {Record<string, Array<{label: string, value: string}>>} props.canonicalTagOptionsByDomain
 * @param {() => void} props.onClose
 * @param {() => void} props.onCreated - Called after a successful save, so the list behind the modal refreshes.
 * @param {() => void} props.onCanonicalTagCreated - Reloads the canonical tag options without closing the form.
 */
const MappingFormModal = ({
  ROOT_URL,
  isOpen,
  fixedTagDomain,
  canonicalTagOptionsByDomain,
  onClose,
  onCreated,
  onCanonicalTagCreated
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [metadataFields, setMetadataFields] = useState([]);
  const [form] = Form.useForm();
  const pickedTagDomain = Form.useWatch("tagDomain", form);

  const tagDomain = fixedTagDomain ?? pickedTagDomain ?? TAG_DOMAIN_FILTER_OPTIONS[0].value;
  const canonicalTagOptions = canonicalTagOptionsByDomain[tagDomain] ?? [];

  /**
   * Posts the new mapping. An empty source tag creates a canonical tag on its
   * own. A refusal that carries a message is shown on the source tag field.
   *
   * @param {object} formValues
   * @param {string} [formValues.sourceTag] - Empty or absent for a canonical tag on its own.
   * @param {string} formValues.canonicalTag
   */
  const handleSubmit = ({ sourceTag, canonicalTag, tagDomain: submittedTagDomain, ...metadata }) => {
    setIsSaving(true);
    axios
      .post(`${ROOT_URL}/${TAGS_API_BASE}`, {
        tagDomain: fixedTagDomain ?? submittedTagDomain,
        sourceTag,
        canonicalTag,
        metadata
      })
      .then(() => {
        showSuccessNotification("Mapping added successfully!");
        onCreated();
      })
      .catch((error) => {
        const { message } = error?.response?.data ?? {};
        if (!message) {
          showErrorNotification(error);
          return;
        }

        form.setFields([{ name: "sourceTag", errors: [message] }]);
      })
      .finally(() => setIsSaving(false));
  };

  /**
   * Clears the canonical tag when the type changes, since the picked tag
   * belongs to the previous type's list.
   */
  const handleTagDomainChange = () => form.setFieldsValue({ canonicalTag: undefined });

  useEffect(() => {
    axios
      .get(`${ROOT_URL}/${TAGS_API_BASE}/${METADATA_FIELDS_ENDPOINT_PATH}`, { params: { tagDomain } })
      .then((response) => setMetadataFields(response.data.metadataFields))
      .catch(showErrorNotification);
  }, [ROOT_URL, tagDomain]);

  return (
    <Modal
      title="Add tag mapping"
      open={isOpen}
      onCancel={onClose}
      okText="Add mapping"
      confirmLoading={isSaving}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={CREATE_MAPPING_INFO_TEXT} />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ tagDomain: TAG_DOMAIN_FILTER_OPTIONS[0].value }}
        onFinish={handleSubmit}
      >
        {!fixedTagDomain && (
          <Form.Item
            name="tagDomain"
            label="Type"
            extra="The product line this mapping belongs to. It decides which canonical tags you can pick."
            rules={[{ required: true, message: "Pick a type." }]}
          >
            <Select options={TAG_DOMAIN_FILTER_OPTIONS} onChange={handleTagDomainChange} />
          </Form.Item>
        )}
        <Form.Item
          name="sourceTag"
          label="Source tag"
          extra="The tag exactly as the source publishes it. Leave empty to add a canonical tag on its own."
        >
          <Input placeholder="dp" maxLength={200} />
        </Form.Item>
        <Form.Item
          name="canonicalTag"
          label="Canonical tag"
          rules={[{ required: true, message: "Pick or add the tag this maps to." }]}
        >
          <CanonicalTagSelect
            ROOT_URL={ROOT_URL}
            tagDomain={tagDomain}
            canonicalTagOptions={canonicalTagOptions}
            disabled={false}
            placeholder="Pick a canonical tag"
            onCanonicalTagCreated={onCanonicalTagCreated}
          />
        </Form.Item>
        <MetadataFields metadataFields={metadataFields} />
      </Form>
    </Modal>
  );
};

export default MappingFormModal;
